import { floating, radiusCva, shadowCva, useProvider } from "@overmap-ai/blocks"
import type { SuggestionProps } from "@tiptap/suggestion"
import { cx } from "class-variance-authority"
import {
	type ComponentPropsWithoutRef,
	type Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState,
} from "react"

import { genericMemo } from "../../../utils/optimization"
import { MentionListContext } from "./context"
import { menuContent } from "./cva"
import type { MentionListRef, MentionListVariantProps } from "./typings"

export interface MentionListRootProps<TItem>
	extends SuggestionProps<TItem, TItem>, ComponentPropsWithoutRef<"ul">, MentionListVariantProps {
	ref?: Ref<MentionListRef<TItem>>
}

export const MentionListRoot = genericMemo(<TItem,>(props: MentionListRootProps<TItem>) => {
	const providerContext = useProvider()
	const {
		children,
		className,

		// SuggestionProps
		ref,
		items,
		command,
		editor: _editor,
		range: _range,
		query: _query,
		text: _text,
		decorationNode: _decorationNode,
		clientRect: _clientRect,

		// style props
		size = "md",
		variant = "soft",
		radius = providerContext.radius,
		accentColor = "base",
		...rest
	} = props

	const [activeIndex, setActiveIndex] = useState(0)

	const selectItem = useCallback(
		(item: TItem) => {
			command(item)
		},
		[command],
	)

	const upHandler = useCallback(() => {
		setActiveIndex((activeIndex + items.length - 1) % items.length)
	}, [items.length, activeIndex])

	const downHandler = useCallback(() => {
		setActiveIndex((activeIndex + 1) % items.length)
	}, [items.length, activeIndex])

	const enterHandler = useCallback(() => {
		const issue = items[activeIndex]

		selectItem(issue)
	}, [items, selectItem, activeIndex])

	const isActive = useCallback(
		(item: TItem) => {
			return item === items[activeIndex]
		},
		[activeIndex, items],
	)

	useEffect(() => setActiveIndex(0), [items])

	useImperativeHandle(ref, () => ({
		onKeyDown: ({ event }) => {
			switch (event.key) {
				case "ArrowUp":
					event.stopPropagation()
					event.preventDefault()
					upHandler()
					return true
				case "ArrowDown":
					event.stopPropagation()
					event.preventDefault()
					downHandler()
					return true
				case "Enter":
					event.stopPropagation()
					event.preventDefault()
					enterHandler()
					return true
				default:
					return false
			}
		},
	}))

	const contextValue = useMemo(
		() => ({
			isActive,
			selectItem: props.command,
			empty: items.length === 0,
			// style props
			size,
			variant,
			radius,
		}),
		[isActive, items.length, props.command, radius, size, variant],
	)

	return (
		<ul
			className={cx(
				menuContent({ size }),
				floating({ side: "bottom" }),
				shadowCva({ size: "md" }),
				radiusCva({ radius, maxLarge: true }),
				className,
			)}
			role="menu"
			data-side="bottom"
			data-accent-color={accentColor}
			{...rest}
		>
			<MentionListContext value={contextValue}>{children}</MentionListContext>
		</ul>
	)
})
